import React, { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useClickOutside } from "../../hooks"
import { ChevronDown, User } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "../../../../../lib"
import CircularCheckbox from "../ui/CircularCheckbox"

const UserSelector = ({ users = [], className, selectedUserId, onChange }) => {
  // hooks
  const { t } = useTranslation()

  // local states
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [visibleCount, setVisibleCount] = useState(20)

  // refs
  const dropdownRef = useRef(null)

  const filteredUsers = useMemo(() => {
    const query = search.toLowerCase()
    return users.filter(
      (u) => u.name.toLowerCase().includes(query) || u.company_name.toLowerCase().includes(query)
    )
  }, [search, users])

  const visibleUsers = filteredUsers.slice(0, visibleCount)

  //
  useClickOutside(dropdownRef, () => setIsOpen(false))

  // handlers

  return (
    <div className={cn("flex items-center justify-center z-50", className)} ref={dropdownRef}>
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-52 cursor-pointer bg-white border border-bg-text-secondary rounded-t-md px-2 py-1 flex items-center justify-between hover:border-text-secondary/30 transition-colors"
        >
          <div className="flex items-center gap-3 text-text-main min-w-0 flex-1">
            <User className="w-4 h-4 flex-shrink-0" />
            <span className="text-xs truncate block">
              {users.find((u) => u.id === selectedUserId)?.name || t("admin.select_user")}
            </span>
          </div>
          <ChevronDown
            className={cn(
              "w-4 h-4 text-text-secondary transition-transform flex-shrink-0 ml-2",
              isOpen && "rotate-180"
            )}
          />
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              key="dropdown"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="absolute top-full right-0 mt-2 bg-white  rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto overflow-x-hidden w-80"
            >
              <div className="px-2 py-1 border-b border-text-secondary/30">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    setVisibleCount(20)
                  }}
                  placeholder={t("filter.find")}
                  className="w-full px-2 py-1 text-xs"
                />
              </div>

              {visibleUsers.map((user) => {
                const { id } = user
                const isSelected = id === selectedUserId

                return (
                  <button
                    type="button"
                    key={id}
                    onClick={() => {
                      onChange(id)
                      setIsOpen(false)
                    }}
                    className={cn(
                      "w-full px-2 py-1 text-left transition-colors text-xs rounded-md text-text-main cursor-pointer flex items-center truncate",
                      "hover:bg-second-success/30",
                      selectedUserId === id && "font-bold"
                    )}
                  >
                    <CircularCheckbox checked={isSelected} className="gap-2 pointer-events-none">
                      <span className="truncate block">
                        {user.company_name} - {user.name}
                      </span>
                    </CircularCheckbox>
                  </button>
                )
              })}

              {filteredUsers.length > visibleCount && (
                <button
                  onClick={() => setVisibleCount((v) => v + 20)}
                  className="w-full py-2 text-xs text-center text-second-success/50 hover:text-second-success cursor-pointer transition"
                >
                  {t("admiralsite.show_more")}
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default UserSelector
